import { DEFAULT_WEATHER_SETTINGS, WeatherSettings } from '@fe/weather';
import { UdViewModel, UdViewWeatherSettings } from './udview';

function extractKeys(keyPath: string[], object: any, list: string[][]) {
    const value = object[keyPath[keyPath.length - 1]];
    if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
        list.push([...keyPath]);
    } else {
        for (const key of Object.keys(value)) {
            extractKeys([...keyPath, key], value, list);
        }
    }
}

function extractKeyPaths<T>(keys: Array<Extract<keyof T, string>>, userSettings: T) {
    const paths: string[][] = [];
    for (const key of keys) {
        extractKeys([key], userSettings, paths);
    }
    return paths;
}

const weatherPaths = extractKeyPaths(
    // Include only this subset and dsua min level, the rest of dsua settings are part of the static data in ud view model
    [
        'animation',
        'altitude',
        'airport',
        'airmetsSigmets',
        'airspace',
        'radar',
        'turbulenceIcing',
        'winds',
        'dsua',
        'misc',
        'lightning',
        'sigWx'
    ],
    DEFAULT_WEATHER_SETTINGS
)
    .filter((x) => x[0] !== 'dsua' || x[1] === 'minimumLevel') // Include dsua.minimumLevel
    .map((path) => ({
        key: path.join('.'),
        setter: function (target: any, value: any) {
            for (let i = 0; i < path.length - 1; i++) {
                target = target[path[i]];
            }
            target[path[path.length - 1]] = value;
        },
        getter: (target: any) => path.reduce((p, c) => p[c], target)
    }));

export function weatherSettingsToUdView(userSettings: WeatherSettings): UdViewWeatherSettings {
    const modified: { key: string; value: string }[] = [];
    for (const path of weatherPaths) {
        const defaultValue = path.getter(DEFAULT_WEATHER_SETTINGS);
        const user = path.getter(userSettings);
        const userJson = JSON.stringify(user);
        const defaultJson = JSON.stringify(defaultValue);
        if (defaultJson !== userJson) {
            modified.push({ key: path.key, value: JSON.stringify(user) });
        }
    }

    return modified;
}

export function weatherSettingsFromUdView(view: UdViewModel): WeatherSettings {
    const settings = structuredClone(DEFAULT_WEATHER_SETTINGS);

    if (view.weather.settings) {
        const udViewSettings = view.weather.settings;

        for (const entry of weatherPaths) {
            const idx = udViewSettings.findIndex((x) => x.key === entry.key);
            if (idx < 0) {
                continue;
            }
            const value = JSON.parse(udViewSettings[idx].value);

            // This is not a fatal error because in default settings there may be a null and in actual setting there might be a value
            // Since we're dealing with a dynamically typed language typechecking is limited
            // if (typeof value !== typeof entry.getter(DEFAULT_SETTINGS)) {
            //     console.warn('Type mismatch between default settings and user setting, key: %s', entry.key);
            // }

            entry.setter(settings, value);
        }
    }
    // dsua settings are part of the dsua object
    settings.dsua.show = view.staticData.sua.show && view.staticData.sua.dynamic;
    settings.dsua.alert = view.staticData.sua.alert;
    settings.dsua.caution = view.staticData.sua.caution;
    settings.dsua.danger = view.staticData.sua.danger;
    settings.dsua.militaryArea = view.staticData.sua.militaryArea;
    settings.dsua.prohibited = view.staticData.sua.prohibited;
    settings.dsua.restricted = view.staticData.sua.restricted;
    settings.dsua.training = view.staticData.sua.traning;
    settings.dsua.warning = view.staticData.sua.warning;
    settings.dsua.unknown = view.staticData.sua.unkown;
    settings.dsua.filterGroup = view.staticData.sua.filterGroup;

    return settings;
}
